import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const LoadingContext = createContext();

export const useLoading = () => useContext(LoadingContext);

/**
 * LoadingProvider - Maneja el estado de loading global con soporte para pasos y fases
 * 
 * OPTIMIZACIONES:
 * - Todos los callbacks memoizados con useCallback para estabilidad de referencias
 * - Value del context memoizado con useMemo para evitar re-renders innecesarios
 * - Funciones puras que no recrean objetos innecesariamente
 * 
 * Backward compatible: openLoading() sin pasos muestra spinner clásico
 * Nuevo: openSteps(stepDefs) muestra UI de pasos con progreso
 * 
 * Fases:
 * - processing: Flujo normal, header azul, barra de progreso azul/verde
 * - rollback: Error detectado, se inyectan pasos de anulación, header rojo/naranja
 * - completed: Éxito total, botón "Cerrar" visible, header verde
 * - failed: Rollback terminado, botón "Cerrar" visible, header rojo con resumen del error
 * - aborted: Operación cancelada pre-DTE, botón "Cerrar" visible
 */
export const LoadingProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [steps, setSteps] = useState([]);
    const [phase, setPhaseState] = useState('processing');
    const [summaryMessage, setSummaryMessageState] = useState(null);
    const [actionButtons, setActionButtonsState] = useState([]);
    // step = { id: string, label: string, status: 'pending'|'loading'|'done'|'error'|'warning'|'skipped', message?: string }
    // phase = 'processing' | 'rollback' | 'completed' | 'failed' | 'aborted'
    // actionButton = { label: string, onClick: function, color: string, icon?: string }

    /**
     * Abre el loading modal sin pasos (backward compatible)
     * MEMOIZADO: referencia estable
     */
    const openLoading = useCallback(() => {
        setIsLoading(true);
        setSteps([]);
        setPhaseState('processing');
        setSummaryMessageState(null);
        setActionButtonsState([]);
    }, []);

    /**
     * Abre el loading modal con pasos definidos
     * MEMOIZADO: referencia estable
     * @param {Array} stepDefs - Array de { id: string, label: string }
     */
    const openSteps = useCallback((stepDefs) => {
        setSteps(stepDefs.map(s => ({ ...s, status: 'pending', message: undefined })));
        setIsLoading(true);
        setPhaseState('processing');
        setSummaryMessageState(null);
        setActionButtonsState([]);
    }, []);

    /**
     * Actualiza el estado de un paso específico
     * MEMOIZADO: referencia estable
     * @param {string} stepId - ID del paso a actualizar
     * @param {string} status - Nuevo estado: 'pending'|'loading'|'done'|'error'|'warning'|'skipped'
     * @param {string} [message] - Mensaje opcional para mostrar debajo del paso
     */
    const updateStep = useCallback((stepId, status, message) => {
        setSteps(prev => prev.map(s => 
            s.id === stepId 
                ? { ...s, status, message: message !== undefined ? message : s.message }
                : s
        ));
    }, []);

    /**
     * Cambia la fase del modal
     * MEMOIZADO: referencia estable
     * @param {string} newPhase - 'processing'|'rollback'|'completed'|'failed'|'aborted'
     */
    const setPhase = useCallback((newPhase) => {
        setPhaseState(newPhase);
    }, []);

    /**
     * Define el mensaje resumen final
     * MEMOIZADO: referencia estable
     * @param {string} message - Mensaje a mostrar
     */
    const setSummaryMessage = useCallback((message) => {
        setSummaryMessageState(message);
    }, []);

    /**
     * Define botones de acción personalizados
     * MEMOIZADO: referencia estable
     * @param {Array} buttons - Array de { label: string, onClick: function, color: string, icon?: string }
     */
    const setActionButtons = useCallback((buttons) => {
        setActionButtonsState(buttons);
    }, []);

    /**
     * Inyecta pasos nuevos al final (para rollback)
     * MEMOIZADO: referencia estable
     * @param {Array} newStepDefs - Array de { id: string, label: string }
     */
    const addSteps = useCallback((newStepDefs) => {
        setSteps(prev => [
            ...prev,
            ...newStepDefs.map(s => ({ ...s, status: 'pending', message: undefined }))
        ]);
    }, []);

    /**
     * Cierra el loading modal y limpia los pasos
     * MEMOIZADO: referencia estable
     */
    const closeLoading = useCallback(() => {
        setIsLoading(false);
        setSteps([]);
        setPhaseState('processing');
        setSummaryMessageState(null);
        setActionButtonsState([]);
    }, []);

    // MEMOIZAR el value del context para evitar re-renders innecesarios en consumers
    const value = useMemo(() => ({
        isLoading,
        steps,
        phase,
        summaryMessage,
        actionButtons,
        openLoading,
        openSteps,
        updateStep,
        setPhase,
        setSummaryMessage,
        setActionButtons,
        addSteps,
        closeLoading
    }), [
        isLoading,
        steps,
        phase,
        summaryMessage,
        actionButtons,
        openLoading,
        openSteps,
        updateStep,
        setPhase,
        setSummaryMessage,
        setActionButtons,
        addSteps,
        closeLoading
    ]);

    return (
        <LoadingContext.Provider value={value}>
            {children}
        </LoadingContext.Provider>
    );
};
